import { Copy, MoreHorizontal, Trash2 } from 'lucide-react'
import { Badge } from '@/components/ui/Badge'
import {
  type Column,
  DataTable,
  type FilterDef,
  type TableState,
} from '@/components/blocks/data-table'
import { Button } from '@/components/ui/Button'
import { DropdownMenu, DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/DropdownMenu'
import { Progress } from '@/components/ui/Progress'

import { count } from '@/lib'
import type { GiftCode } from '@/models'

const STATUS_VARIANT = {
  Active: 'success',
  Expired: 'neutral',
  Exhausted: 'warning',
} as const

export const CODE_COLUMNS: Column<GiftCode>[] = [
  {
    key: 'code',
    header: 'Code',
    width: '160px',
    sortable: true,
    cell: (row) => <span className="text-fg text-sm font-semibold tracking-wide">{row.code}</span>,
  },
  { key: 'reward', header: 'Reward', width: 'minmax(160px,1.2fr)', sortable: true },
  {
    key: 'redeemed',
    header: 'Redeemed',
    width: '220px',
    sortable: true,
    cell: (row) => (
      <span className="flex items-center gap-2">
        <Progress value={row.redeemed} max={row.limit} className="w-20" />
        <span className="text-fg-subtle text-sm tabular-nums">
          {count(row.redeemed)} / {count(row.limit)}
        </span>
      </span>
    ),
  },
  {
    key: 'expires',
    header: 'Expires',
    width: '120px',
    align: 'end',
    sortable: true,
    cell: (row) => <span className="text-fg-muted text-sm">{row.expires}</span>,
  },
  {
    key: 'status',
    header: 'Status',
    sortable: true,
    cell: (row) => (
      <Badge dot variant={STATUS_VARIANT[row.status]}>
        {row.status}
      </Badge>
    ),
  },
]

export const CODE_FILTERS: FilterDef[] = [
  {
    key: 'status',
    label: 'All statuses',
    options: [
      { value: '', label: 'All statuses' },
      { value: 'Active', label: 'Active' },
      { value: 'Expired', label: 'Expired' },
      { value: 'Exhausted', label: 'Exhausted' },
    ],
  },
]

interface CodesTableProps {
  rows: readonly GiftCode[]
  total: number
  state: TableState
  onStateChange: (patch: Partial<TableState>) => void
  onCopy: (code: GiftCode) => void
  onRevoke: (code: GiftCode) => void
  empty?: React.ReactNode
}

export function CodesTable({
  rows,
  total,
  state,
  onStateChange,
  onCopy,
  onRevoke,
  empty,
}: CodesTableProps) {
  return (
    <DataTable
      columns={CODE_COLUMNS}
      rows={rows}
      total={total}
      state={state}
      onStateChange={onStateChange}
      rowKey={(row) => row.id}
      empty={empty}
      minWidth={860}
      rowActions={(row) => (
        <DropdownMenu
          trigger={
            <Button variant="ghost" size="icon" aria-label={`Actions for ${row.code}`}>
              <MoreHorizontal />
            </Button>
          }
        >
          <DropdownMenuItem onClick={() => onCopy(row)}>
            <Copy />
            Copy Code
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem destructive onClick={() => onRevoke(row)}>
            <Trash2 />
            Revoke Code
          </DropdownMenuItem>
        </DropdownMenu>
      )}
    />
  )
}
